import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as signOutOfFirebaseAuth,
} from "firebase/auth";

/**
 * D7 — client-side Firebase sign-in (the Portal SSO pattern, §9.1). The web authenticates against the
 * shared Portal Firebase project (local: senetek-portal-dev; prod: senetek-portal), gets a Firebase ID
 * token, and exchanges it at the API's /auth/session for a SeneSight session. Config is read from
 * VITE_FIREBASE_* env (public Firebase web config — never a secret; see docs/DOGFOOD-LOCAL.md), never
 * hard-coded here.
 */
/** Read the PUBLIC Firebase web config from VITE_FIREBASE_* at call time (not module-load) so a test
 * — or a lazily-set env — sees the current values, and firebaseConfigured() reflects them. */
function firebaseConfig(): {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
} {
  const env = import.meta.env as unknown as Record<string, string | undefined>;
  return {
    apiKey: env["VITE_FIREBASE_API_KEY"],
    authDomain: env["VITE_FIREBASE_AUTH_DOMAIN"],
    projectId: env["VITE_FIREBASE_PROJECT_ID"],
    storageBucket: env["VITE_FIREBASE_STORAGE_BUCKET"],
    messagingSenderId: env["VITE_FIREBASE_MESSAGING_SENDER_ID"],
    appId: env["VITE_FIREBASE_APP_ID"],
  };
}

/** True only when the config needed for the enabled auth flow is fully present. Google popup/redirect
 * sign-in FAILS at runtime without authDomain ('auth/auth-domain-config-required', the RS.0.19-follow-on
 * incident where a partial apiKey+projectId bundle shipped a broken login), so authDomain is required
 * here alongside apiKey and projectId — a partial config renders the not-configured screen instead. */
export function firebaseConfigured(): boolean {
  const c = firebaseConfig();
  return Boolean(c.apiKey && c.authDomain && c.projectId);
}

function app(): FirebaseApp {
  if (!firebaseConfigured()) {
    throw new Error("Firebase is not configured — set VITE_FIREBASE_* (see docs/DOGFOOD-LOCAL.md)");
  }
  return getApps()[0] ?? initializeApp(firebaseConfig());
}

/** The SeneSight session the API returns after verifying the Firebase ID token. */
export interface ExchangedSession {
  readonly token: string;
  readonly subject: string;
  readonly tenant: string;
  readonly role: string;
}

/**
 * Sign in via Google (Portal SSO), then exchange the Firebase ID token for a SeneSight session.
 * The ID token is sent as a Bearer credential to the public /auth/session endpoint, which verifies it
 * (JWKS) and resolves the principal's tenant + role from membership.
 */
export async function signInWithGoogle(): Promise<ExchangedSession> {
  const credential = await signInWithPopup(getAuth(app()), new GoogleAuthProvider());
  return exchangeIdToken(await credential.user.getIdToken());
}

/** Use the same Firebase project and SeneSight session exchange for Microsoft accounts. */
export async function signInWithMicrosoft(): Promise<ExchangedSession> {
  const credential = await signInWithPopup(getAuth(app()), new OAuthProvider("microsoft.com"));
  return exchangeIdToken(await credential.user.getIdToken());
}

export function isEmailSignInLink(url: string): boolean {
  return isSignInWithEmailLink(getAuth(app()), url);
}

export async function sendEmailSignInLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(getAuth(app()), email, {
    handleCodeInApp: true,
    url: `${window.location.origin}/login`,
  });
}

export async function completeEmailSignInLink(
  email: string,
  url: string,
): Promise<ExchangedSession> {
  const credential = await signInWithEmailLink(getAuth(app()), email, url);
  return exchangeIdToken(await credential.user.getIdToken());
}

const EMAIL_LINK_STORAGE_KEY = "senesight.emailForSignIn";

export function rememberEmailForLink(email: string): void {
  try {
    window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, email);
  } catch {
    // Private browsing or blocked storage: the return page will ask for the address.
  }
}

export function readRememberedEmail(): string | null {
  try {
    return window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function forgetRememberedEmail(): void {
  try {
    window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
  } catch {
    // The sign-in can still finish without writable storage.
  }
}

/**
 * NX-3433: renew the SeneSight session from the Firebase user who signed in, without a popup.
 * Firebase refreshes its own ID token (and refuses one for a disabled or revoked account), and the
 * API re-checks membership on the exchange, so a renewal never outlives the identity behind it.
 * Returns null when there is no Firebase user to renew from (signed out, or not configured).
 */
export async function renewWithFirebase(): Promise<ExchangedSession | null> {
  if (!firebaseConfigured()) return null;
  const auth = getAuth(app());
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;
  return exchangeIdToken(await user.getIdToken());
}

/** NX-3433: end the Firebase sign-in too, so nothing can renew a session the user ended. */
export async function signOutOfFirebase(): Promise<void> {
  if (!firebaseConfigured()) return;
  await signOutOfFirebaseAuth(getAuth(app())).catch(() => undefined);
}

async function exchangeIdToken(idToken: string): Promise<ExchangedSession> {
  const res = await fetch("/api/v1/auth/session", {
    headers: { authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `sign-in failed (HTTP ${res.status})`);
  }
  return (await res.json()) as ExchangedSession;
}
